import React, { useEffect } from "react";

const PaymentSummary = ({
  items = [],
  isLineDiscountApplied,
  setGrandTotal,
  grandTotal,
  taxRates = { cgstRate: 0, sgstRate: 0, igstRate: 0 }, // Tax rates passed as a prop
  discountValue,
  setDiscountValue,
  discountType,
  setDiscountType,
  isPublished,
}) => {
  const calculateTotals = () => {
    // Calculate totals for all items
    const itemTotals = items.reduce(
      (totals, item) => {
        const quantity = Number(item.quantity) || 0;
        const unitPrice = Number(item.sellingPrice) || 0;
        const taxableValue = quantity * unitPrice;

        const lineDiscount =
          item.lineDiscountType === "percentage"
            ? (taxableValue * (item.lineDiscountValue || 0)) / 100
            : item.lineDiscountValue || 0;

        const netTaxableValue = taxableValue - lineDiscount;

        // Calculate taxes based on the taxable value (before discount)
        const cgst =
          (taxableValue * (item.cgstRate || taxRates.cgstRate)) / 100;
        const sgst =
          (taxableValue * (item.sgstRate || taxRates.sgstRate)) / 100;
        const igst =
          (taxableValue * (item.igstRate || taxRates.igstRate)) / 100;

        const itemTax = cgst + sgst + igst;

        return {
          subtotal: totals.subtotal + taxableValue,
          totalDiscount: totals.totalDiscount + lineDiscount,
          totalTaxableValue: totals.totalTaxableValue + netTaxableValue,
          totalTax: totals.totalTax + itemTax,
          lineCgst: totals.lineCgst + cgst,
          lineSgst: totals.lineSgst + sgst,
          lineIgst: totals.lineIgst + igst,
        };
      },
      {
        subtotal: 0,
        totalDiscount: 0,
        totalTaxableValue: 0,
        totalTax: 0,
        lineCgst: 0,
        lineSgst: 0,
        lineIgst: 0,
      }
    );

    // Apply overall discount to the total taxable value if no line discounts are applied
    let overallDiscount = 0;
    let finalTaxableValue = itemTotals.totalTaxableValue;

    if (!isLineDiscountApplied && discountValue > 0) {
      overallDiscount =
        discountType === "percentage"
          ? (itemTotals.totalTaxableValue * discountValue) / 100
          : discountValue;

      finalTaxableValue = Math.max(
        itemTotals.totalTaxableValue - overallDiscount,
        0
      );
    }

    return {
      ...itemTotals,
      totalTaxableValue: finalTaxableValue,
      overallDiscount,
    };
  };

  // Calculate all totals based on items and discounts
  const {
    subtotal,
    totalDiscount,
    totalTaxableValue,
    overallDiscount,
    totalTax,
  } = calculateTotals();

  // Grand Total is the sum of taxable value after discount and the total tax
  const calculatedGrandTotal = totalTaxableValue + totalTax;

  // console.log("Total Taxable Value", totalTaxableValue);

  // Update the grand total in the parent component whenever it changes
  useEffect(() => {
    setGrandTotal(calculatedGrandTotal);
  }, [
    calculatedGrandTotal,
    setGrandTotal,
    totalTaxableValue,
    totalTax,
    discountType,
    discountValue,
  ]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mt-6">
      <h3 className="text-2xl font-bold text-gray-800 mb-6">Payment Summary</h3>
      <div className="space-y-4 text-gray-700">
        <div className="flex justify-between items-center">
          <p className="text-lg">Subtotal (Before Discount):</p>
          <p className="text-lg font-semibold">₹{subtotal.toFixed(2)}</p>
        </div>

        {isLineDiscountApplied ? (
          <div className="text-red-500 text-sm">
            Line-level discounts applied. Overall discount disabled.
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <label className="text-lg">Overall Discount:</label>
            <div className="flex items-center space-x-2">
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                disabled={isLineDiscountApplied || isPublished}
                className="px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-green-300 focus:border-green-500"
              >
                <option value="lumpSum">Lump Sum</option>
                <option value="percentage">Percentage</option>
              </select>
              <input
                type="number"
                readOnly={isPublished}
                value={discountValue}
                onChange={(e) => setDiscountValue(Number(e.target.value))}
                disabled={isLineDiscountApplied}
                className="w-24 px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-green-300 focus:border-green-500"
                min="0"
                max={discountType === "percentage" ? "100" : undefined}
              />
              <span>{discountType === "percentage" ? "%" : "₹"}</span>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
          <p className="text-lg">Total Discount:</p>
          <p className="text-lg font-semibold">₹{totalDiscount.toFixed(2)}</p>
        </div>

        {overallDiscount > 0 && (
          <div className="flex justify-between items-center">
            <p className="text-lg">Overall Discount Applied:</p>
            <p className="text-lg font-semibold">
              -₹{overallDiscount.toFixed(2)}
            </p>
          </div>
        )}

        <div className="flex justify-between items-center">
          <p className="text-lg">Total Taxable Value (After Discounts):</p>
          <p className="text-lg font-semibold">
            ₹{totalTaxableValue.toFixed(2)}
          </p>
        </div>

        <div className="flex justify-between items-center">
          <p className="text-lg font-bold">Total Tax:</p>
          <p className="text-lg font-bold text-gray-800">
            ₹{totalTax.toFixed(2)}
          </p>
        </div>

        <hr className="my-4 border-gray-300" />

        <div className="flex justify-between items-center">
          <p className="text-xl font-bold text-gray-900">Grand Total:</p>
          <p className="text-xl font-bold text-green-600">
            ₹{calculatedGrandTotal.toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;
